package com.nonopn.skywatcher.design.widgets.abslists

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyGridItemScope
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.material3.pulltorefresh.rememberPullToRefreshState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.nonopn.skywatcher.design.utils.LocalFrame
import com.nonopn.skywatcher.design.utils.WindowType
import com.nonopn.skywatcher.design.widgets.defaultBackground
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.platform.Platform
import eu.codlab.platform.currentPlatform
import kotlin.math.min

@OptIn(ExperimentalFoundationApi::class, ExperimentalMaterial3Api::class)
@Composable
fun <T : AbstractListModel<M, E>, M : AbstractListModelState<E>, E> AbstractLists(
    model: T,
    textWhenEmptyList: String? = null,
    header: (@Composable () -> Unit)? = null,
    renderElement: @Composable LazyGridItemScope.(Int, E) -> Unit
) {
    val pullToRefreshState = rememberPullToRefreshState()
    val state by model.states.collectAsState()
    val modelState = state.modelState

    val modifier = Modifier
        .fillMaxSize()
        .defaultBackground()

    println("AbstractLists, showing ${state.modelState.filtered.size} items")

    Column(
        modifier = modifier
    ) {
        header?.invoke()
        Box(
            modifier = Modifier.fillMaxSize(),
        ) {
            // add pull to refresh on iOS & Android only
            if (listOf(Platform.IOS, Platform.ANDROID).contains(currentPlatform)) {
                PullToRefreshBox(
                    isRefreshing = listOf(
                        ListModelState.DEFAULT,
                        ListModelState.LOADING
                    ).contains(modelState.state),
                    modifier = Modifier.fillMaxSize(),
                    state = pullToRefreshState,
                    onRefresh = {
                        model.load()
                    }
                ) {
                    ShowLoaded(
                        Modifier.fillMaxSize(),
                        state,
                        renderElement
                    )
                }
            } else {
                ShowLoaded(
                    Modifier.fillMaxSize(),
                    state,
                    renderElement
                )
            }

            when (modelState.state) {
                ListModelState.ERROR -> ShowError(modifier)
                ListModelState.DEFAULT -> ShowLoading(Modifier.fillMaxSize())
                ListModelState.LOADING -> ShowLoading(Modifier.fillMaxSize())
                ListModelState.LOADED -> {
                    if (null != textWhenEmptyList && state.modelState.objects.isEmpty()) {
                        // ShowEmpty(text = textWhenEmptyList)
                    }
                }
            }
        }
    }
}

@Composable
fun ShowLoading(modifier: Modifier) {
    Box(
        modifier = modifier.padding(16.dp),
        contentAlignment = Alignment.Center
    ) {
        CircularProgressIndicator()
    }
}

@Composable
fun rememberColumns(max: Int = 3): Int {
    val columns = when (LocalFrame.current) {
        WindowType.SMARTPHONE_TINY -> min(max, 1)
        WindowType.SMARTPHONE -> min(max, 1)
        WindowType.PHABLET -> min(max, 2)
        WindowType.TABLET -> max
    }

    return columns
}

@Suppress("MagicNumber")
@Composable
fun <E> ShowLoaded(
    modifier: Modifier,
    state: AbstractListModelState<E>,
    renderElement: @Composable LazyGridItemScope.(Int, E) -> Unit
) {
    val filtered = state.modelState.filtered

    val columns = rememberColumns()

    LazyVerticalGrid(
        modifier = modifier,
        state = rememberLazyGridState(),
        columns = GridCells.Fixed(columns)
    ) {
        items(filtered.size, key = { filtered[it].hashCode() }) {
            renderElement(0, filtered[it])
        }
    }
}

@Composable
fun ShowError(modifier: Modifier) = TextNormal(
    modifier = modifier,
    text = "error"
)
